import EarthFilled16 from '@carbon/icons-react/es/earth--filled/16';
import moment, { ISO_8601 } from 'moment';
import React, { FC, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { RelativeTime } from 'components/atoms/relative-time';
import { Page } from 'components/molecules/page';
import { documentTemplateTools } from 'components/organisms/document/parts/topbar/tools';
import { TemplateUnsavedPrompt } from 'components/organisms/templates/commons/prompt';
import { TemplateRocketlaneDocument } from 'components/organisms/templates/commons/rocketlane-document';
import { Caption01, Text02 } from 'components/styles';
import { RoutePaths } from 'constant';
import { setDirtyState } from 'data/templates';
import { TemplateState } from 'data/templates/api';
import { updateDocumentTemplateAction } from 'data/templates/documents/actions';
import { useGetDocumentById } from 'data/templates/documents/hooks/use-get-document-by-id';
import { useTranslation } from 'hooks/use-translation';
import { getTemplatesQueryKey } from 'queries';
import { fullName } from 'utils/full-name';
import { TemplateBreadcrump } from '../commons';

const Content = styled(Page.Content)`
  height: calc(100% - 64px);
`;

const HeaderWrapper = styled.div`
  display: flex;
  width: 100%;
`;

const SaveWrapper = styled.div`
  margin-top: -24px;
  display: flex;
  align-items: center;
`;

const Title = styled.span`
  flex: 1;
  font-size: var(--heading-5-size);
  font-weight: var(--heading-5-weight);
  line-height: var(--heading-3-line-height);
`;

const LastSavedText = styled.span`
  ${Caption01};
  ${Text02};

  margin-right: var(--layout-02);
`;

export const DocumentTemplateDetailPage: FC = () => {
  const baseTitle = useTranslation('templates.document');
  const queryClient = useQueryClient();
  const { templateId } = useParams<{ templateId: string }>();
  const dispatch = useDispatch();
  const [isSaving, toggleIsSaving] = useState(false);
  const documentTemplateStore = useGetDocumentById(parseInt(templateId, 10));

  const docId = parseInt(templateId, 10);

  const onTitleChange = (title: string) => {
    if (documentTemplateStore?.template) {
      const template = {
        templateId: docId,
        templateName: title,
      };

      dispatch(
        updateDocumentTemplateAction({
          template,
        }),
      );
    }
  };

  const onSaveTemplate = () => {
    toggleIsSaving(true);

    dispatch(
      updateDocumentTemplateAction({
        sync: true,
        template: {
          templateId: docId,
          state: TemplateState.Published,
        },
        handleAlways: () => {
          toggleIsSaving(false);

          dispatch(
            setDirtyState({
              id: docId,
              _state: false,
            }),
          );

          queryClient.invalidateQueries(getTemplatesQueryKey());
        },
      }),
    );
  };

  const publish = () => onSaveTemplate();

  return (
    <Page>
      <Helmet>
        <title>{`${baseTitle} | ${
          documentTemplateStore?.template?.templateName || ''
        }`}</title>
      </Helmet>
      <TemplateUnsavedPrompt />
      <Page.Header
        title={
          <HeaderWrapper>
            <Title>{documentTemplateStore?.template?.templateName}</Title>
            <SaveWrapper>
              {documentTemplateStore?.template?.publishedAt && (
                <LastSavedText data-cy="templates.last_saved_at">
                  <FormattedMessage
                    id="templates.last_saved_at"
                    values={{
                      by:
                        documentTemplateStore?.template?.publishedBy &&
                        fullName(documentTemplateStore?.template?.publishedBy),
                      time: (
                        <RelativeTime
                          time={moment(
                            documentTemplateStore?.template?.publishedAt,
                            ISO_8601,
                          )}
                        />
                      ),
                    }}
                  />
                </LastSavedText>
              )}
              <Button
                data-cy="templates.save"
                type="primary"
                iconPlacement="right"
                icon={<EarthFilled16 />}
                loading={isSaving}
                onClick={publish}
              >
                <FormattedMessage id="save" />
              </Button>
            </SaveWrapper>
          </HeaderWrapper>
        }
        breadcrumbs={
          <TemplateBreadcrump
            route={RoutePaths.documentTemplateRoot}
            id="templates.documents"
          />
        }
      />
      <Content>
        <TemplateRocketlaneDocument
          docId={docId}
          editorProps={{
            readOnly: false,
          }}
          onTitleChange={onTitleChange}
          tools={documentTemplateTools}
        />
      </Content>
    </Page>
  );
};
